import { useEffect, useState } from 'react';
import { kindLabel } from '../lib/driverKind';
import { Link } from 'react-router-dom';
import { api, type SalaryStatusLine as SalaryLine } from '../lib/api';
import Modal from '../components/Modal';
import { link } from '../lib/deepLink';
import HeldReqBanner from '../components/HeldReqBanner';
import { DataTable, ErrorBox, Pager, loadAllPages, usePageSize, dash, money, useList, type Column, type Row } from '../components/DataTable';
import { canScreen, useMe } from '../lib/me';
import { useAutoSearch } from '../lib/search';

/**
 * 정산현황 — **명세 생성의 대상을 정하는 화면**.
 *
 * 여기서 '정산'을 켠 사람에게만 명세가 만들어진다(`result_salary.is_salary`).
 * 이 화면이 없으면 그 게이트를 운영할 수 없다 — 레거시에서 체크된 상태를 그대로 따를 수밖에 없었다.
 *
 * ⚠️ **금액은 "지금 켜면 만들어질 명세 금액"이다.** 명세 생성과 같은 계산식·같은 게이트를 쓴다.
 *    레거시 그리드는 재무 항목(재무추가·월정기·할부금)을 0 으로 내려주는데(SP 안에서 주석 처리된 것으로 보인다),
 *    그 숫자는 실제 명세 차인지급액과 다르다. 그래서 여기서는 **영업계·재무계를 나란히** 보여준다.
 *
 * ⚠️ 체크를 끄는 것은 **상태 저장일 뿐** 원장을 지우지 않는다. 레거시는 즉시 삭제하지만,
 *    체크 해제가 조용히 원장을 지우면 되돌리기 어렵다. 지우려면 명세 생성에서 그 달을 다시 만든다.
 */


export default function SettleStatus({ driveMon }: { driveMon: string }) {
  const { me } = useMe();
  const canEdit = canScreen(me, 'settle');

  // 마지막에 고른 축을 PC 에 기억한다(#251 — 인사팀은 늘 관리기사A 라 매번 한 번 더 눌러야 했다).
  const [axis, setAxisState] = useState<'owner' | 'direct'>(() => {
    try { return localStorage.getItem('settle-status:axis') === 'direct' ? 'direct' : 'owner'; } catch { return 'owner'; }
  });
  const setAxis = (a: 'owner' | 'direct') => {
    setAxisState(a);
    try { localStorage.setItem('settle-status:axis', a); } catch { /* 저장 못 해도 이번 화면엔 적용 */ }
  };
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [onlySalary, setOnlySalary] = useState(false);
  const [page, setPage] = useState(0);
  const [PAGE, setPAGE] = usePageSize('settle-status', 50);

  // 치는 대로 찾는다(입력이 멎으면 적용). 버튼·Enter 는 그대로 — `lib/search.ts` 참고.
  useAutoSearch(term, (v) => { setQ(v); setPage(0); });
  const [nonce, setNonce] = useState(0);
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** 줄 보기로 연 사람(행 누르기 · 2026-10-02). */
  const [open, setOpen] = useState<Row | null>(null);

  const { rows, loading, total } = useList(
    () =>
      api.listResultSalaryStatus({
        driveMon, axis, q: q || undefined, onlySalary: onlySalary || undefined,
        limit: PAGE, offset: page * PAGE,
      }) as unknown as Promise<{ rows: Row[]; total?: Record<string, unknown> }>,
    [driveMon, axis, q, onlySalary, page, PAGE, nonce],
  );

  async function toggle(r: Row, next: boolean) {
    const id = Number(r.target_id);
    setBusy(id);
    setError(null);
    try {
      await api.setResultSalary({
        driveMon,
        ...(axis === 'owner' ? { ownerId: id } : { driverId: id }),
        isSalary: next,
      });
      setNonce((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }

  const columns: Column[] = [
    {
      key: 'is_salary',
      label: '정산',
      render: (r) => {
        const on = r.is_salary === true;
        if (!canEdit) {
          return on
            ? <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700">대상</span>
            : <span className="text-ink-faint">—</span>;
        }
        return (
          <label className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={on}
              disabled={busy === Number(r.target_id) || loading}
              onChange={(e) => toggle(r, e.target.checked)}
            />
            <span className={`text-xs ${on ? 'text-emerald-700' : 'text-ink-faint'}`}>
              {busy === Number(r.target_id) ? '…' : on ? '대상' : ''}
            </span>
          </label>
        );
      },
    },
    // phone: 폰에선 이름을 왼쪽에 고정하고 +/− 세부 칸·변경은 뺀다 — 영업계·재무계·차인지급액만 남긴다(모바일 7차).
    { key: 'name', label: axis === 'owner' ? '차주' : '기사', phone: 'pin' },
    ...(axis === 'direct' ? [{ key: 'driver_kind', label: '기사구분', render: (r) => kindLabel(r.driver_kind) } as Column] : []),
    { key: 'drive_cost', label: '운행료', align: 'right', render: (r) => money(r.drive_cost) },
    { key: 'req_plus', label: '영업 +', align: 'right', phone: 'hide', render: (r) => money(r.req_plus) },
    { key: 'req_minus', label: '영업 −', align: 'right', phone: 'hide', render: (r) => money(r.req_minus) },
    { key: 'cancel_plus', label: '취소수당', align: 'right', phone: 'hide', render: (r) => money(r.cancel_plus) },
    { key: 'cancel_minus', label: '취소공제', align: 'right', phone: 'hide', render: (r) => money(r.cancel_minus) },
    {
      key: 'sales_total', label: '영업계', align: 'right',
      render: (r) => <b>{money(r.sales_total)}</b>,
    },
    { key: 'daily_plus', label: '재무추가 +', align: 'right', phone: 'hide', render: (r) => money(r.daily_plus) },
    { key: 'daily_minus', label: '재무추가 −', align: 'right', phone: 'hide', render: (r) => money(r.daily_minus) },
    // 월정기 + (#251 — 직영기사는 급여가 월정기 지급으로 들어가 이 칸이 없으면 차인지급액이 어디서 왔는지 안 보인다)
    { key: 'mo_plus', label: '월정기 +', align: 'right', phone: 'hide', render: (r) => money(r.mo_plus) },
    { key: 'mo_minus', label: '월정기 −', align: 'right', phone: 'hide', render: (r) => money(r.mo_minus) },
    { key: 'fin_inst', label: '할부금', align: 'right', phone: 'hide', render: (r) => money(r.fin_inst) },
    {
      key: 'fin_total', label: '재무계', align: 'right',
      render: (r) => {
        const v = Number(r.fin_total ?? 0);
        return <span className={v < 0 ? 'text-red-600' : ''}>{money(r.fin_total)}</span>;
      },
    },
    {
      key: 'grand_total', label: '차인지급액', align: 'right',
      render: (r) => <b>{money(r.grand_total)}</b>,
    },
    { key: 'updated_by', label: '변경', phone: 'hide', render: (r) => <span className="text-xs">{dash(r.updated_by)}</span> },
  ];

  if (!driveMon) {
    return <p className="text-sm text-ink-faint">정산월을 고르면 정산현황이 보입니다.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-1">
          {(
            [
              ['owner', '차주'],
              ['direct', '관리기사A'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              onClick={() => { setAxis(id); setPage(0); }}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                axis === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <label className="flex items-center gap-1.5 text-sm text-ink-soft">
          <input
            type="checkbox"
            checked={onlySalary}
            onChange={(e) => { setOnlySalary(e.target.checked); setPage(0); }}
          />
          정산 대상만
        </label>

        <form
          onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); setPage(0); }}
          className="ml-auto flex items-center gap-2"
        >
          <input
            className="field w-72"
            placeholder={axis === 'owner' ? '차주명 (여러 명은 띄어서)' : '기사명 (여러 명은 띄어서)'}
            title="이름을 띄어쓰기로 여러 개 넣으면 그 사람들만 보입니다"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
          <button className="btn-search" type="submit" aria-label="검색" title="검색" />
        </form>
      </div>

      <p className="text-xs text-ink-soft">
        ‘정산’을 켠 대상에게만 명세가 만들어집니다. 금액은 <b>지금 켜면 만들어질 명세 금액</b>이며,
        영업계(운행료·영업정산)와 재무계(재무추가·월정기·할부금)를 나눠 보여줍니다.
        체크를 꺼도 이미 만들어진 명세는 지워지지 않습니다. <b>줄을 누르면</b> 그 달 줄이 원천별로 펼쳐집니다.
      </p>

      {/* 🔴 청구 전이라 이 달 정산현황·명세에 안 들어가는 영업정산(OPS 선지급 등 · 2026-10-02 way). */}
      <HeldReqBanner driveMon={driveMon} context="status" />

      {error && <ErrorBox message={error} />}
      {!canEdit && <p className="text-xs text-ink-faint">정산 대상 변경은 정산 권한이 필요합니다.</p>}

      <DataTable columns={columns} rows={rows} loading={loading} total={total}
                 tableId={`settle.status.${axis}`} onRowClick={(r) => setOpen(r)}
                 exportXlsx={{
                   name: `정산현황_${driveMon}`,
                   loadAll: () => loadAllPages((limit, offset) =>
                     api.listResultSalaryStatus({ driveMon, axis, q: q || undefined, onlySalary: onlySalary || undefined, limit, offset })
                       .then((r) => (r as unknown as { rows: Row[] }).rows)),
                 }} />
      {open && (
        <Modal width="max-w-4xl">
          <SalaryLinesView driveMon={driveMon} axis={axis} row={open} onClose={() => setOpen(null)} />
        </Modal>
      )}
      <Pager page={page} setPage={setPage} rowCount={rows.length} pageSize={PAGE} loading={loading} setPageSize={setPAGE} baseSize={50} />
    </div>
  );
}

/** 줄 보기 묶음 — 순서·이름 = 목록 칸. key 는 서버 `LINE_GROUP`(resultSalary.ts) 과 같은 글자. */
const GROUPS: { key: string; label: string; col: string | null; minus?: boolean }[] = [
  { key: 'drive', label: '운행료', col: 'drive_cost' },
  { key: 'drive_info', label: '운행료(관리기사A · 기록만, 합계 밖)', col: null },
  { key: 'req_plus', label: '영업정산 +', col: 'req_plus' },
  { key: 'req_minus', label: '영업정산 −', col: 'req_minus', minus: true },
  { key: 'cancel_plus', label: '취소수당', col: 'cancel_plus' },
  { key: 'cancel_minus', label: '취소공제', col: 'cancel_minus', minus: true },
  { key: 'daily_plus', label: '재무추가 +', col: 'daily_plus' },
  { key: 'daily_minus', label: '재무추가 −', col: 'daily_minus', minus: true },
  { key: 'mo_plus', label: '월정기 +', col: 'mo_plus' },
  { key: 'mo_minus', label: '월정기 −', col: 'mo_minus', minus: true },
  { key: 'fin_inst', label: '할부금', col: 'fin_inst', minus: true },
  { key: 'none', label: '합계 밖(계정 없는 영업정산)', col: null },
];

const chip = 'rounded border border-line px-1.5 py-0.5 text-[11px] text-brand-700 hover:bg-brand-50';

/** 영업정산 줄의 청구상태(그 접수·그 달 미수) — 청구완료 초록 · 청구대상(청구 요청) 파랑 · 청구 없음 주황. */
function MisuChip({ state }: { state: string | null }) {
  // 청구 대기 목록은 「청구상태/입금상태, …」 모양으로 온다 — 청구상태만 추린다.
  const s = [...new Set((state || '').split(', ').map((x) => x.split('/')[0]!.trim() || '청구 없음'))].join(', ') || '청구 없음';
  const tone = s.includes('청구완료') ? 'bg-emerald-50 text-emerald-700'
    : s.includes('청구대상') ? 'bg-sky-50 text-sky-700'
    : 'bg-amber-50 text-amber-800';
  return <span className={`rounded px-1.5 py-0.5 text-[11px] ${tone}`} title="청구상태">{s}</span>;
}

/** 원천 화면으로 가는 링크(있는 것만). 누르면 창을 닫고 그 화면으로 — 같은 정산년월로 연다. */
function LineLinks({ l, mon, onGo }: { l: SalaryLine; mon: string; onGo: () => void }) {
  const out: [string, string][] = [];
  if (l.kind === 'Req' && l.src_id) out.push(['영업정산', link.resultReq(l.src_id, mon)]);
  if (l.kind === 'Req' && l.ops_dy_no) {
    if (l.ops_cash_book_id) out.push(['출납일보', link.cashBook(l.ops_cash_book_id, l.ops_cash_date)]);
    out.push([l.ops_dy_no, link.opsTransit(l.ops_dy_no)]);
  }
  if (l.kind === 'Drive' && l.link_recv_id) out.push([`접수 ${l.link_recv_id}`, link.dispatch(l.link_recv_id)]);
  if (l.kind === 'Daily' && l.src_id) out.push([`재무추가 NO ${l.src_id}`, link.resultDaily(mon, l.src_id)]);
  if (l.kind === 'Monthly' && (l.car_no || l.driver_name)) out.push(['월정기', link.resultMo(mon, String(l.car_no ?? l.driver_name))]);
  if (l.kind === 'FinInst' && l.car_no) out.push(['할부금', link.finInst(l.car_no)]);
  if (!out.length) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {out.map(([t, to]) => <Link key={to} to={to} className={chip} onClick={onGo}>{t}</Link>)}
    </span>
  );
}

/**
 * 정산현황 한 사람의 그 달 줄 — 원천별로 묶고, 묶음 합이 목록 칸과 같은지 옆에 적는다.
 * 금액은 서버가 목록과 같은 생성 문장으로 낸다(`listResultSalaryLines`) — 화면은 더하지 않고 서버 합(grp_sum)을 쓴다.
 */
export function SalaryLinesView({ driveMon, axis, row, onClose }: { driveMon: string; axis: 'owner' | 'direct'; row: Row; onClose: () => void }) {
  const [lines, setLines] = useState<SalaryLine[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [held, setHeld] = useState<Record<string, unknown>[] | null>(null);
  useEffect(() => {
    let alive = true;
    api.listResultSalaryLines({ driveMon, axis, targetId: Number(row.target_id) })
      .then((r) => { if (alive) setLines(r.rows); })
      .catch((e) => { if (alive) setErr(e instanceof Error ? e.message : String(e)); });
    // 이 사람 몫인데 청구 전이라 빠진 영업정산(#251 — 레거시는 청구상태 칸으로 보며 「청구대상이면 반영 안 됨」을 확인했다).
    api.listResultReqHeld({ driveMon, axis, targetId: Number(row.target_id) })
      .then((r) => { if (alive) setHeld(r.rows); })
      .catch(() => { if (alive) setHeld([]); });
    return () => { alive = false; };
  }, [driveMon, axis, row.target_id]);

  return (
    <div className="card space-y-3 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-base font-bold">{String(row.name ?? '')} · {driveMon} 줄 보기</h2>
        <span className="text-xs text-ink-soft">{axis === 'owner' ? '차주' : '관리기사A'} · 차인지급액 <b>{money(row.grand_total)}</b></span>
        <button className="btn-ghost ml-auto" onClick={onClose}>닫기</button>
      </div>
      <p className="text-xs text-ink-soft">
        목록 칸과 같은 계산(명세 생성 문장)으로 낸 줄입니다. 묶음 합이 목록 칸과 다르면 빨갛게 표시합니다. 링크를 누르면 그 화면에서 같은 정산년월로 열립니다.
      </p>
      {err && <ErrorBox message={err} />}
      {!lines && !err && <p className="text-sm text-ink-faint">불러오는 중…</p>}
      {lines && lines.length === 0 && <p className="text-sm text-ink-faint">이 달 줄이 없습니다.</p>}
      {held && held.length > 0 && (
        <section className="rounded-lg border border-amber-300">
          <header className="flex flex-wrap items-center gap-2 border-b border-amber-300 bg-amber-50 px-3 py-1.5 text-sm">
            <b>청구 전이라 이 달 반영 안 된 영업정산</b>
            <span className="text-xs text-ink-soft">{held.length}줄 · 위 합계·차인지급액에 안 들어갑니다 — 그 접수가 청구되면 들어갑니다</span>
          </header>
          <ul className="max-h-60 divide-y divide-line overflow-y-auto">
            {held.map((h, i) => {
              const minus = Number(h.driver_minus ?? 0) !== 0;
              return (
                <li key={`${String(h.result_req_id)}-${i}`} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-1 text-xs">
                  <span className="font-medium">{dash(h.account)}</span>
                  <span className="text-ink-soft">{[h.cust_name, h.car_no, h.driver_name].filter(Boolean).join(' · ')}</span>
                  <span className="min-w-0 flex-1" />
                  <MisuChip state={(h.misu_state as string | null) ?? null} />
                  <span className={`font-medium tabular-nums ${minus ? 'text-red-700' : ''}`}>
                    {minus ? `−${money(h.driver_minus)}` : money(h.driver_plus)}
                  </span>
                  {h.result_req_id != null && (
                    <Link to={link.resultReq(Number(h.result_req_id), driveMon)} className={chip} onClick={onClose}>영업정산</Link>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}
      {lines && GROUPS.map((g) => {
        const ls = lines.filter((l) => l.grp === g.key);
        if (!ls.length) return null;
        const sum = ls[0]!.grp_sum;
        const want = g.col ? row[g.col] : null;
        const same = g.col === null || Math.round(Number(want ?? 0)) === Math.round(Number(sum));
        return (
          <section key={g.key} className="rounded-lg border border-line">
            <header className="flex flex-wrap items-center gap-2 border-b border-line bg-canvas px-3 py-1.5 text-sm">
              <b>{g.label}</b>
              <span className="text-xs text-ink-soft">{ls.length}줄</span>
              <span className={`ml-auto font-semibold tabular-nums ${g.minus ? 'text-red-700' : ''}`}>{money(sum)}</span>
              {g.col && (
                <span className={`text-[11px] ${same ? 'text-emerald-700' : 'font-bold text-red-700'}`} title="정산현황 목록의 같은 칸 값">
                  {same ? '= 목록 칸' : `≠ 목록 칸 ${money(want)}`}
                </span>
              )}
            </header>
            <ul className="max-h-72 divide-y divide-line overflow-y-auto">
              {ls.map((l, i) => (
                <li key={`${l.section}-${l.src_id ?? 'x'}-${i}`} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-3 py-1 text-xs">
                  <span className="font-medium">{dash(l.account_name)}</span>
                  {(l.car_no || l.driver_name) && <span className="text-ink-soft">{[l.car_no, l.driver_name].filter(Boolean).join(' · ')}</span>}
                  <span className="min-w-0 max-w-full flex-1 truncate text-ink-soft" title={l.comment ?? ''}>{l.comment ?? ''}</span>
                  {l.kind === 'Req' && <MisuChip state={l.misu_state} />}
                  <span className="font-medium tabular-nums">{money(l.amt)}</span>
                  <LineLinks l={l} mon={driveMon} onGo={onClose} />
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}
